/**
 * useArchiveImport - Archive import operations for BufferSyncContext
 *
 * Extracted from BufferSyncContext.tsx. Handles importing archive nodes,
 * threads, transcriptions, and plain text into buffers.
 *
 * @module @humanizer/studio/hooks/useArchiveImport
 */

import { useCallback, type Dispatch, type SetStateAction, type MutableRefObject } from 'react';
import type { ContentItem, ArchiveNode, StackItem } from '../contexts/buffer-sync-types';
import { injectMediaRefs } from '../utils/media-url';
import { createLogger } from '../utils/debug';

const log = createLogger('ArchiveImport');

export interface ArchiveImportDeps {
  isElectron: boolean;
  api: {
    overrides: {
      getBatch: (nodeIds: string[]) => Promise<{ overrides: Record<string, { editedText: string }> }>;
    };
  };
  archiveApi: {
    isLocal: boolean;
  };
  activeBufferIdRef: MutableRefObject<string | null>;
  setActiveBufferName: Dispatch<SetStateAction<string>>;
  setWorkingContent: Dispatch<SetStateAction<ContentItem[]>>;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  setSyncError: Dispatch<SetStateAction<string | null>>;
  workingContentRef: MutableRefObject<ContentItem[]>;
  setContent: (content: ContentItem[]) => Promise<void>;
  appendContent: (items: ContentItem[]) => Promise<void>;
  commit: (message: string) => Promise<void>;
  // Content history
  isRestoringHistory: MutableRefObject<boolean>;
  pushContentHistory: (label: string) => void;
  // Transform chain
  setTransformChain: Dispatch<SetStateAction<Array<{ id: string; label: string; type: string; timestamp: number }>>>;
  setActiveChainBufferId: Dispatch<SetStateAction<string | null>>;
  // Stack
  addToStackRef: MutableRefObject<(item: ContentItem, bufferId?: string) => Promise<void>>;
}

export interface ArchiveImportReturn {
  importArchiveNode: (node: ArchiveNode) => Promise<void>;
  importArchiveThread: (thread: { id: string; title: string }, nodes: ArchiveNode[]) => Promise<void>;
  importTranscription: (mediaId: string, text: string, metadata: {
    type: string; versionId: string; model: string; filename?: string;
  }, bufferId?: string) => Promise<void>;
  importText: (text: string, title?: string, metadata?: Record<string, unknown>) => Promise<void>;
  getTextContent: () => string;
}

export function useArchiveImport(deps: ArchiveImportDeps): ArchiveImportReturn {
  const {
    isElectron,
    api,
    archiveApi,
    activeBufferIdRef,
    setActiveBufferName,
    setSyncError,
    workingContentRef,
    setContent,
    appendContent,
    commit,
    isRestoringHistory,
    pushContentHistory,
    setTransformChain,
    setActiveChainBufferId,
    addToStackRef,
  } = deps;

  const importText = useCallback(async (
    text: string,
    title?: string,
    metadata?: Record<string, unknown>
  ) => {
    const item: ContentItem = {
      id: crypto.randomUUID(),
      type: 'text',
      text,
      metadata: {
        source: {
          type: 'direct',
          path: [title ?? 'Imported Text'],
        },
        timestamp: Date.now(),
        wordCount: text.split(/\s+/).filter(Boolean).length,
        ...metadata,
      },
    };

    await appendContent([item]);
  }, [appendContent]);

  const importArchiveNode = useCallback(async (node: ArchiveNode) => {
    const mediaRefs = node.mediaRefs as Array<{ id: string; type: string }> | undefined;
    let text = injectMediaRefs(node.text, mediaRefs);
    let hasOverride = false;

    // Merge override if exists (Electron local or cloud)
    if (isElectron && window.electronOverride) {
      try {
        const override = await window.electronOverride.get(node.id);
        if (override) {
          text = override.editedText;
          hasOverride = true;
        }
      } catch {
        // Override fetch failed — use original text
      }
    } else if (!archiveApi.isLocal) {
      try {
        const { overrides } = await api.overrides.getBatch([node.id]);
        if (overrides[node.id]) {
          text = overrides[node.id].editedText;
          hasOverride = true;
        }
      } catch {
        // Override fetch failed — use original text
      }
    }

    const item: ContentItem = {
      id: crypto.randomUUID(),
      type: 'archive-node',
      text,
      metadata: {
        source: {
          type: node.sourceType ?? node.type ?? 'archive',
          path: [node.threadTitle ?? 'Archive', node.id],
          nodeId: node.id,
          threadId: node.threadId,
          threadTitle: node.threadTitle,
        },
        authorRole: node.authorRole,
        timestamp: node.timestamp,
        wordCount: text.split(/\s+/).filter(Boolean).length,
        hasOverride,
      },
    };

    // Push current content onto history before replacing
    if (!isRestoringHistory.current) {
      pushContentHistory(`Message: ${node.text.slice(0, 40).replace(/\n/g, ' ')}…`);
    }

    // Clear transform chain — new content, not a transform
    setTransformChain([]);
    setActiveChainBufferId(null);

    // ELECTRON: Create a DB-backed buffer from archive node
    if (isElectron && window.electronBuffer) {
      try {
        let saved;
        // Media items and other synthetic nodes don't exist in archive_nodes table —
        // skip loadFromArchive and go straight to createFromText
        if (node.type !== 'media' && node.type !== 'text') {
          try {
            saved = await window.electronBuffer.loadFromArchive(node.id);
          } catch {
            // Not in archive_nodes — will create from text below
          }
        }
        if (!saved) {
          saved = await window.electronBuffer.createFromText(text, {
            format: 'markdown',
            initialState: 'staged',
            sourcePlatform: node.sourceType ?? node.type,
            authorRole: node.authorRole,
            metadata: { nodeId: node.id, threadId: node.threadId, threadTitle: node.threadTitle },
          });
        }
        activeBufferIdRef.current = saved.id;
        item.metadata.bufferId = saved.id;
        // Update active buffer name to match what's displayed
        const displayName = node.threadTitle?.slice(0, 50) ?? text.slice(0, 50).split('\n')[0] ?? 'Content';
        setActiveBufferName(displayName);
      } catch (err) {
        log.error('Buffer creation failed:', err);
        setSyncError('Could not create persistent buffer — content shown but not saved');
      }
    }

    // Always add to stack (accumulative model) — bufferId already exists from archive import
    await addToStackRef.current(item, activeBufferIdRef.current ?? undefined);

    // Replace workspace content
    await setContent([item]);
  }, [api.overrides, setContent, archiveApi.isLocal, isElectron, isRestoringHistory, pushContentHistory, setTransformChain, setActiveChainBufferId, activeBufferIdRef, setActiveBufferName, setSyncError, addToStackRef]);

  const importArchiveThread = useCallback(async (
    thread: { id: string; title: string },
    nodes: ArchiveNode[]
  ) => {
    // Batch-fetch overrides for all nodes in the thread (Electron local or cloud)
    let overrides: Record<string, { editedText: string }> = {};
    const nodeIds = nodes.map(n => n.id);
    if (isElectron && window.electronOverride) {
      try {
        overrides = await window.electronOverride.getBatch(nodeIds);
      } catch {
        // Override fetch failed — use original text for all
      }
    } else if (!archiveApi.isLocal) {
      try {
        const result = await api.overrides.getBatch(nodeIds);
        overrides = result.overrides;
      } catch {
        // Override fetch failed — use original text for all
      }
    }

    const items: ContentItem[] = nodes.map((node, index) => {
      const override = overrides[node.id];
      const text = override
        ? override.editedText
        : injectMediaRefs(node.text, node.mediaRefs as Array<{ id: string; type: string }> | undefined);

      return {
        id: crypto.randomUUID(),
        type: 'archive-node' as const,
        text,
        metadata: {
          source: {
            type: node.sourceType ?? node.type ?? 'archive',
            path: [thread.title, node.id],
            nodeId: node.id,
            threadId: thread.id,
            threadTitle: thread.title,
          },
          authorRole: node.authorRole,
          timestamp: node.timestamp,
          wordCount: text.split(/\s+/).filter(Boolean).length,
          messagePosition: index,
          totalMessages: nodes.length,
          hasOverride: !!override,
          mediaRefs: node.mediaRefs,
          format: node.format,
          sourceMetadata: node.sourceMetadata,
        },
      };
    });

    // Push current content onto history before replacing
    if (!isRestoringHistory.current) {
      pushContentHistory(`Thread: ${thread.title.slice(0, 40)}`);
    }

    // Clear transform chain — new content, not a transform
    setTransformChain([]);
    setActiveChainBufferId(null);

    // Update active buffer name to thread title
    if (isElectron) {
      setActiveBufferName(thread.title.slice(0, 50));
    }

    await setContent(items);
  }, [api.overrides, setContent, archiveApi.isLocal, isElectron, isRestoringHistory, pushContentHistory, setTransformChain, setActiveChainBufferId, setActiveBufferName]);

  const importTranscription = useCallback(async (
    mediaId: string,
    text: string,
    meta: { type: string; versionId: string; model: string; filename?: string },
    bufferId?: string,
  ) => {
    const item: ContentItem = {
      id: crypto.randomUUID(),
      type: 'transcription',
      text,
      metadata: {
        source: {
          type: 'transcription',
          path: ['Transcriptions', meta.filename ?? mediaId],
        },
        mediaId,
        versionId: meta.versionId,
        transcriptionType: meta.type,
        model: meta.model,
        timestamp: Date.now(),
        wordCount: text.split(/\s+/).filter(Boolean).length,
      },
    };

    const targetBufferId = bufferId ?? activeBufferIdRef.current ?? undefined;
    await addToStackRef.current(item, targetBufferId);
    await commit(`Transcription: ${meta.type} of ${meta.filename ?? mediaId}`);
  }, [activeBufferIdRef, addToStackRef, commit]);

  const getTextContent = useCallback((): string => {
    return workingContentRef.current.map(item => item.text).join('\n\n');
  }, [workingContentRef]);

  return {
    importArchiveNode,
    importArchiveThread,
    importTranscription,
    importText,
    getTextContent,
  };
}
